import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import ArtboardCell from 'components/ArtboardCell';
import ColumnResizer from 'components/ColumnResizer';
import * as Models from 'models';
import { isRegularRelation } from 'utils/relations/isRegularRelation';
import { areThereHorizontalNeighbors } from 'utils/rowsHeight/areThereHorizontalNeighbors';
import { toPx } from 'utils/toPx';
import Row from '../Row';
import { ColumnProps } from './models';
import { columnSelector } from './selectors';
import styles from './styles.module.scss';

const Column: React.FunctionComponent<ColumnProps> = (props) => {

  const {
    activeLayoutIds,
    columnsCount,
    disableCellWidthEditing = false,
    editMode,
    height,
    isLayoutEmpty,
    isOpenToolbar,
    isReusableLayout,
    isSectionActive,
    layout,
    layoutId,
    layoutRelations,
    level,
    relation,
    sectionStyles,
    setRowHeightSilently,
    showColumnResizer,
    ssi,
    toggleExpandedMode,
    toggleLayoutRelations,
    width,
    dataTestId,
  } = props;
  const columnRelationId = relation.get('id');
  const relationIds = relation.get('relationIds');
  const onlyChild = !areThereHorizontalNeighbors(columnRelationId, layoutRelations);
  const rowsHeight = relation.getIn(['styles', 'rowsHeight']);
  const container = React.createRef<HTMLDivElement>();
  const { activeLayer, isReadOnly } = useSelector(columnSelector);

  return (
    <div
      className={classNames(styles.Column, {
        [styles.stretchable]: height || !rowsHeight.every(Boolean),
      })}
      style={{ width: width ? toPx(width) : '100%' }}
      ref={container}
      data-testid={dataTestId}
    >
      {
        showColumnResizer && !isReadOnly &&
        <ColumnResizer
          container={container}
          layoutId={layoutId}
          relationIsBeingResizedId={columnRelationId}
          relations={layoutRelations}
          toggleRelations={toggleLayoutRelations}
        />}
      {
        relationIds.map((relationId, cellPosition) => {
          const currentRelation = layoutRelations.get(relationId) as Models.LayeredRegularRelationMap | Models.RowRelationMap;
          const currentHeight = rowsHeight.get(cellPosition);
          const isLastCell = cellPosition === relationIds.size - 1;
          const showRowResizer = !isOpenToolbar && (onlyChild ? true : !isLastCell);

          if (isRegularRelation(currentRelation)) {
            return (
              <ArtboardCell
                cellPosition={cellPosition}
                dataTestId={dataTestId}
                index={cellPosition}
                cellsCount={relationIds.size}
                columnRelationId={columnRelationId}
                columnsCount={columnsCount}
                disableCellWidthEditing={disableCellWidthEditing}
                height={currentHeight}
                isLayoutEmpty={isLayoutEmpty}
                isReusableLayout={isReusableLayout}
                isSectionActive={isSectionActive}
                key={relationId}
                layoutId={layoutId}
                layout={layout}
                layoutRelations={layoutRelations}
                notEditable={!!ssi || isReadOnly}
                parentRelationId={columnRelationId}
                positionWithinColumn={cellPosition}
                relation={currentRelation}
                sectionStyles={sectionStyles}
                setRowHeightSilently={setRowHeightSilently}
                showRowResizer={showRowResizer}
                toggleExpandedMode={toggleExpandedMode}
                toggleLayoutRelations={toggleLayoutRelations}
                width={width}
                activeLayer={activeLayer}
              />
            );
          }

          return (
            <Row
              activeLayoutIds={activeLayoutIds}
              columnRelationId={columnRelationId}
              disableCellWidthEditing={columnsCount < 2}
              editMode={editMode}
              height={currentHeight}
              isLast={isLastCell}
              isLayoutEmpty={isLayoutEmpty}
              isOpenToolbar={isOpenToolbar}
              isReusableLayout={isReusableLayout}
              isSectionActive={isSectionActive}
              key={relationId}
              layout={layout}
              layoutId={layoutId}
              layoutRelations={layoutRelations}
              level={level + 1}
              positionWithinColumn={cellPosition}
              relation={currentRelation}
              sectionStyles={sectionStyles}
              setRowHeightSilently={setRowHeightSilently}
              showRowResizer={showRowResizer}
              ssi={ssi}
              toggleExpandedMode={toggleExpandedMode}
              toggleLayoutRelations={toggleLayoutRelations}
              dataTestId={dataTestId}
            />
          );
        })
      }
    </div>
  );
};

export default Column;
